import axios, {AxiosInstance} from 'axios';

import {globalT} from "../lang";
import config from "./../config";
import {getJwtData} from "../commons/helpers/jwtHelper";
import {AxiosRequestConfigExtended} from "./network.types";
import StorageHelper, {StorageKeys} from "../commons/helpers/StorageHelper";
import NotificationManager from "../components/notifications/NotificationManager";
import {
    errorManager,
    toCamelCase,
    toSnakeCase,
    deepMapObject,
    utcToLocaleDate,
    objectToFormData
} from "../commons/helpers/funcHelper";

/**
 * Create a new axios with general options and interceptors already set
 */
export const createAxiosInstance = () => {
    const _customAxios: AxiosInstance =
        axios.create({
            baseURL: config.rpnApiUrl,
        });

    _customAxios.interceptors.request.use(
        async (axiosConfig) => {
            try {
                const _axiosConfig = ({ ...axiosConfig} as AxiosRequestConfigExtended);

                // 1. Set JWT Token
                const jwtData = getJwtData();
                if (jwtData && !_axiosConfig.shouldSkipToken) {
                    _axiosConfig.headers['Authorization'] = `Bearer ${jwtData.accessToken}`;
                }

                // 2. Set language
                const lang = StorageHelper.getItem(StorageKeys.LANGUAGE);
                // @ts-ignore
                _axiosConfig.headers['Accept-Language'] = (lang || config.language.default).toUpperCase();

                // Check if post or put to perform some operation
                if ((_axiosConfig.method === 'patch'
                        || _axiosConfig.method === 'post'
                        || _axiosConfig.method === 'put'
                        || _axiosConfig.method === 'delete')
                    && !_axiosConfig.shouldSkipDataParsing) {
                    // Create an object to store file data
                    const fileData: Record<string, any> = {};

                    // Check if fileData is present
                    if (_axiosConfig.fileData) {
                        // @ts-ignore
                        _axiosConfig.headers['content-type'] = 'multipart/form-data';

                        _axiosConfig.fileData.forEach(f => {
                            fileData[f] = _axiosConfig.data[f];
                            delete _axiosConfig.data[f];
                        });
                    }

                    // Parse object to snakeCase and Form data if its request gets files
                    if (_axiosConfig.fileData) {
                        const data = !_axiosConfig.skipSnakeCaseTransform
                            ? toSnakeCase(_axiosConfig.data) : _axiosConfig.data;
                        // @ts-ignore
                        _axiosConfig.data = objectToFormData(data);
                    } else if (!_axiosConfig.skipSnakeCaseTransform) {
                        _axiosConfig.data = toSnakeCase(_axiosConfig.data);
                    }

                    // Append files to data to send
                    if (_axiosConfig.fileData) {
                        Object.entries(fileData).forEach(item => {
                            let [key, value] = item;
                            if (key.includes('[]') && typeof key === 'string') {
                                let _key: string = JSON.stringify(key.split('[]')[0]) as string;
                                let label = (!_axiosConfig.skipSnakeCaseTransform ? toSnakeCase(_key) : _key) as string;
                                let finalLabel = label+"[]";
                                _axiosConfig.data.append(finalLabel, value, 'file_' + _key);
                            } else {
                                _axiosConfig.data.append(!_axiosConfig.skipSnakeCaseTransform ? toSnakeCase(key) : key, value);
                            }
                        });
                    }
                }

                // Apply changes
                return _axiosConfig;
            } catch (e) {
                return axiosConfig;
            }
        },
        error => Promise.reject(error));

    _customAxios.interceptors.response.use(
        // response => response,
        response => {
            if (response && response.data) {
                // eslint-disable-next-line no-param-reassign
                response.data = deepMapObject(
                    // Data source
                    response.data,
                    // Detect UTC properties
                    key => typeof key === 'string' && /utc$/i.test(key),
                    // Leave key unchanged
                    key => key,
                    // Change value to locale date
                    value => utcToLocaleDate(value)
                );
                //@ts-ignore
                response.data = response.config?.skipResponseAutoFormDataTransform ? response.data : toCamelCase(response.data);
            }

            return response;
        },
        // Intercept any error and display an appropriate message
        error => {
            const originalRequest = error.config;
            // Check if we can handle error
            // By default skipAllErrors is undefined.
            // Non-handling error is for special case of manuel management
            if (!originalRequest.skipAllErrors) {
                // Check if error is defined
                if (error) {
                    // Check if response is defined since it contains error details like status code
                    if (error.response) {
                        switch (error.response.status) {
                            case 400:
                            case 409:
                                // Verify if we have to check this error code
                                if (originalRequest.skipErrorCodes && (
                                    originalRequest.skipErrorCodes.includes(400)
                                    || originalRequest.skipErrorCodes.includes(409)
                                )) break;

                                errorManager(error.response.data.messages);
                                break;
                            case 422:
                                // Verify if we have to check this error code
                                if (originalRequest.skipErrorCodes && (
                                    originalRequest.skipErrorCodes.includes(400)
                                )) break;

                                errorManager(error.response.data.errors);
                                break;
                            case 401:
                            case 419:
                                if (originalRequest.skipErrorCodes && originalRequest.skipErrorCodes.includes(401))
                                    break;

                                NotificationManager.error(globalT("errors.401.feedback"));
                                break;
                            case 403:
                                if(!window.location.href.includes("auth")) {
                                    window.location.reload();
                                }
                                break;
                            case 404:
                                if (originalRequest.skipErrorCodes && originalRequest.skipErrorCodes.includes(404))
                                    break;

                                NotificationManager.error(globalT("errors.404.feedback"));
                                break;
                            case 500:
                                if (originalRequest.skipErrorCodes && originalRequest.skipErrorCodes.includes(500))
                                    break;

                                NotificationManager.error(globalT("errors.500.feedback"));
                                break;
                            case 412:
                                if (originalRequest.skipErrorCodes && originalRequest.skipErrorCodes.includes(412))
                                    break;
                                NotificationManager.error(globalT("errors.500.feedback"));
                                break;
                            case 504:
                                if (originalRequest.skipErrorCodes && originalRequest.skipErrorCodes.includes(504))
                                    break;

                                NotificationManager.error(globalT("errors.504.feedback"));
                                break;
                            default:
                                // Fallback to 500 error
                                NotificationManager.error(globalT("errors.500.feedback"));
                                break;
                        }
                    } else NotificationManager.error(globalT("errors.unknown.feedback"));
                } else NotificationManager.error(globalT("errors.unknown.feedback"));
            }
            return Promise.reject(error);
        });

    return _customAxios;
}

// Initialize the default axios used by the app
const customAxios = createAxiosInstance();

// Export it
export default customAxios;
